import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReceiptView } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  PrintTable,
  printSheet,
} from '@wodi/ui';
import { useEffect, useRef } from 'react';

import { markReceiptPrinted, receiptQuery } from '../api/billing.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { METHOD_LABELS, money } from './format.ts';

/**
 * The receipt's words, in one keyed table so a reviewed Kiswahili version can be added
 * without touching the template (plan §13 decision 21, TODO(verify-spec): translation).
 */
export const RECEIPT_TEXT = {
  en: {
    title: 'Payment receipt',
    depositTitle: 'Deposit receipt',
    depositNote: 'A deposit held against the inpatient stay. It is applied to the final bill.',
    copy: (n: number) => `Copy ${String(n)}`,
    receiptNo: 'Receipt no.',
    date: 'Date',
    patient: 'Patient',
    patientNo: 'Patient no.',
    counterSale: 'Counter sale',
    visitNo: 'Visit no.',
    cashier: 'Cashier',
    item: 'Item',
    qty: 'Qty',
    amount: 'Amount',
    totalPaid: 'Total paid',
    paidBy: 'Paid by',
    reference: 'Reference',
    tendered: 'Cash received',
    change: 'Change',
    balance: 'Balance owed',
    paperReceipt: 'Paper receipt no.',
    // Nothing KRA didn't return is printed (CLAUDE.md, integration rules).
    notSigned: 'This is a payment receipt. The tax invoice is not yet signed through eTIMS.',
    // What eTIMS returned, as returned (docs/plans/phase3-etims-vscu.md §5).
    etims: 'eTIMS',
    signedThrough: (mode: string, at: string) => `Signed through ${mode}, ${at}`,
    signedOffline:
      "Signed offline by the facility's VSCU. It is sent to KRA when the connection returns.",
    kraRefused: 'KRA has not accepted this signature yet.',
    mflCode: (code: string) => `MFL ${code}`,
  },
} as const;

/**
 * Labels for the fields KRA (or the VSCU) returns. Empty until the specification says what
 * they are called and which a receipt must print: the field's own name is printed meanwhile
 * (TODO(verify-spec), plan VSCU-7).
 */
const FISCAL_FIELD_LABELS: Readonly<Record<string, string>> = {};

const MODE_NAMES = { oscu: 'OSCU', vscu: 'VSCU' } as const;

function FiscalLines({ fiscal }: { fiscal: ReceiptView['fiscal'][number] }) {
  if (
    fiscal.state !== 'signed' &&
    fiscal.state !== 'signed_offline' &&
    fiscal.state !== 'transmission_failed'
  )
    return null;
  const mode = fiscal.mode === null ? 'eTIMS' : MODE_NAMES[fiscal.mode];
  return (
    <>
      <PrintLines
        items={[
          {
            label: T.etims,
            value:
              fiscal.signedAt === null
                ? mode
                : T.signedThrough(mode, formatInstant(fiscal.signedAt)),
          },
          ...fiscal.fields.map((f) => ({
            label: FISCAL_FIELD_LABELS[f.name] ?? f.name,
            value: f.value,
            identifier: true,
          })),
        ]}
      />
      {fiscal.state === 'signed_offline' ? <PrintFooter>{T.signedOffline}</PrintFooter> : null}
      {fiscal.state === 'transmission_failed' ? <PrintFooter>{T.kraRefused}</PrintFooter> : null}
    </>
  );
}

/** Whether eTIMS signed this invoice (online, offline, or offline and then refused). */
function fiscallySigned(state: ReceiptView['fiscalStatus']): boolean {
  return state === 'signed' || state === 'signed_offline' || state === 'transmission_failed';
}

const T = RECEIPT_TEXT.en;

export function ReceiptSheet({
  receipt,
  sheetRef,
}: {
  receipt: ReceiptView;
  sheetRef?: React.Ref<HTMLElement>;
}) {
  const copy = receipt.printCount > 0 ? receipt.printCount + 1 : null;
  const p = receipt.payment;
  const title = receipt.purpose === 'deposit' ? T.depositTitle : T.title;
  return (
    <PrintSheet paper="receipt-80mm" label={title} {...(sheetRef ? { ref: sheetRef } : {})}>
      <PrintHeader
        facility={receipt.facility.name}
        facilityLines={
          receipt.facility.mflCode === null ? [] : [T.mflCode(receipt.facility.mflCode)]
        }
        title={copy === null ? title : `${title}, ${T.copy(copy)}`}
      />
      <PrintLines
        items={[
          { label: T.receiptNo, value: receipt.number, identifier: true },
          { label: T.date, value: formatInstant(receipt.issuedAt) },
          ...(receipt.patient === null
            ? [{ label: T.patient, value: T.counterSale }]
            : [
                { label: T.patient, value: receipt.patient.name },
                {
                  label: T.patientNo,
                  value: receipt.patient.patientNumber,
                  identifier: true,
                },
              ]),
          { label: T.visitNo, value: receipt.visitNumber, identifier: true },
          { label: T.cashier, value: receipt.cashier },
          ...(p.manualReceiptReference === null
            ? []
            : [{ label: T.paperReceipt, value: p.manualReceiptReference, identifier: true }]),
        ]}
      />
      <PrintTable>
        <thead>
          <tr>
            <th>{T.item}</th>
            <th data-numeric="">{T.qty}</th>
            <th data-numeric="">{T.amount}</th>
          </tr>
        </thead>
        <tbody>
          {receipt.lines.map((line, index) => (
            <tr key={`${line.invoiceNumber}-${String(index)}`}>
              <td>{line.description}</td>
              <td data-numeric="">{line.quantity}</td>
              <td data-numeric="">{money(line.amount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>{T.totalPaid}</td>
            <td data-numeric="" />
            <td data-numeric="">{money(p.amount)}</td>
          </tr>
        </tfoot>
      </PrintTable>
      <PrintLines
        items={[
          { label: T.paidBy, value: METHOD_LABELS[p.method] },
          ...(p.reference === null
            ? []
            : [{ label: T.reference, value: p.reference, identifier: true }]),
          ...(p.tendered === null ? [] : [{ label: T.tendered, value: money(p.tendered) }]),
          ...(p.change === null ? [] : [{ label: T.change, value: money(p.change) }]),
          { label: T.balance, value: money(receipt.balanceOwed) },
        ]}
      />
      {receipt.purpose === 'deposit' ? <PrintFooter>{T.depositNote}</PrintFooter> : null}
      {receipt.fiscal.map((f) => (
        <FiscalLines key={f.invoiceNumber} fiscal={f} />
      ))}
      {receipt.fiscal.length > 0 && receipt.fiscal.every((f) => fiscallySigned(f.state)) ? null : (
        <PrintFooter>{T.notSigned}</PrintFooter>
      )}
    </PrintSheet>
  );
}

/**
 * Loads a receipt and prints it on the 80 mm printer, then records the print (audited;
 * later prints say "Copy"). With `auto`, the print dialog opens as soon as it is ready:
 * the receipt prints automatically after a payment (plan §13 decision 25).
 */
export function ReceiptPrinter({
  receiptId,
  auto,
  onPrinted,
}: {
  receiptId: string;
  auto: boolean;
  onPrinted?: () => void;
}) {
  const receipt = useQuery(receiptQuery(receiptId));
  const sheet = useRef<HTMLElement>(null);
  const printed = useMutation({
    mutationFn: () => markReceiptPrinted(receiptId),
    onSuccess: () => {
      void receipt.refetch();
      onPrinted?.();
    },
  });
  const started = useRef(false);

  const print = () => {
    if (sheet.current === null) return;
    printSheet(sheet.current);
    printed.mutate();
  };

  useEffect(() => {
    if (auto && receipt.isSuccess && !started.current && sheet.current !== null) {
      started.current = true;
      printSheet(sheet.current);
      printed.mutate();
    }
  }, [auto, receipt.isSuccess, printed]);

  if (receipt.isPending) return <LoadingBlock label="Loading the receipt" rows={3} />;
  if (receipt.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void receipt.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(receipt.error, "The receipt couldn't be loaded. Try again.")}
      </Notice>
    );
  return (
    <div>
      <Button onPress={print} isPending={printed.isPending} pendingLabel="Printing">
        {receipt.data.printCount > 0 ? 'Print a copy' : 'Print receipt'}
      </Button>
      <ReceiptSheet receipt={receipt.data} sheetRef={sheet} />
    </div>
  );
}
